import { useCallback, useMemo, useRef } from 'react';
import { screenToCanvas as screenToCanvasUtil } from '../../../hooks/utils/canvasUtils';
import { QUICK_VIEW_ZOOM_THRESHOLD } from '../constants';

/**
 * Node renderer state: stable pan/zoom refs, nested sprint lookup, and per-node handlers.
 */
export default function useNodeRenderer({
  pan,
  zoom,
  nestedSprints,
  containerRef,
  handleNodeDragStart,
  handleNodeClick,
  handleConnectionDragStart,
  onDeleteTask,
  onCopy,
  onDuplicate,
  handleFocusTask,
  onRemoveNestedSprint,
  onQuickViewMouseEnter,
  onQuickViewMouseLeave,
  onQuickViewMouseMove,
}) {
  const panRef = useRef(pan);
  const zoomRef = useRef(zoom);
  panRef.current = pan;
  zoomRef.current = zoom;

  const isQuickViewEnabled = zoom < QUICK_VIEW_ZOOM_THRESHOLD;

  const nestedSprintMap = useMemo(() => {
    const map = {};
    nestedSprints.forEach(ns => { map[ns.id] = ns; });
    return map;
  }, [nestedSprints]);

  const createNodeHandlers = useCallback((taskId) => ({
    onMouseDown: (e) => handleNodeDragStart(taskId, e),
    onClick: (e) => handleNodeClick(taskId, e),
    onConnectionDragStart: (e, branch, screenPos) => {
      const canvasPos = screenPos
        ? screenToCanvasUtil(screenPos.x, screenPos.y, containerRef, panRef.current, zoomRef.current)
        : null;
      handleConnectionDragStart(taskId, e, branch, canvasPos);
    },
    onDelete: () => onDeleteTask?.(taskId),
    onCopy: () => onCopy?.(taskId),
    onDuplicate: () => onDuplicate?.(taskId),
    onFocusTask: () => handleFocusTask(taskId),
    onRemoveNestedSprint: () => onRemoveNestedSprint?.(taskId),
    onQuickViewMouseEnter: (e) => onQuickViewMouseEnter?.(taskId, e),
    onQuickViewMouseLeave: () => onQuickViewMouseLeave?.(),
    onQuickViewMouseMove: (e) => onQuickViewMouseMove?.(e),
  }), [
    handleNodeDragStart,
    handleNodeClick,
    handleConnectionDragStart,
    containerRef,
    onDeleteTask,
    onCopy,
    onDuplicate,
    handleFocusTask,
    onRemoveNestedSprint,
    onQuickViewMouseEnter,
    onQuickViewMouseLeave,
    onQuickViewMouseMove,
  ]);

  return {
    isQuickViewEnabled,
    nestedSprintMap,
    createNodeHandlers,
  };
}
